/**
 * Passport API client.
 *
 * Fetches the normalized Passport data (career "stations" + "locations") for a
 * CV. The backend (cv-search-service) derives this once from the stored CV text
 * via a cheap LLM and (later) caches it. The Stations page, Map page and the
 * anonymous Explore cover card all render from this single shape.
 */

type ApiCall = (url: string, options?: RequestInit) => Promise<Response>;

const apiBase = () => process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export type StationType = 'education' | 'work' | 'other';
export type LocationKind = 'study' | 'work';

export interface PassportStation {
  year: string | null;
  type: StationType;
  title: string | null;
  org: string | null;
  city: string | null;
  country: string | null;
  /** LLM-predicted coordinates (best-effort, may be null). */
  lat?: number | null;
  lng?: number | null;
}

export interface PassportLocation {
  city: string | null;
  country: string | null;
  kind: LocationKind;
  home: boolean;
  /** LLM-predicted coordinates (best-effort, may be null). */
  lat?: number | null;
  lng?: number | null;
}

export interface PassportSkillGroup {
  group: string;
  items: string[];
}

export interface CvPassport {
  cv_id: string;
  stations: PassportStation[];
  locations: PassportLocation[];
  skills: PassportSkillGroup[];
  languages: string[];
  interests: string[];
  highlights: string[];
  generated_at: number;
}

/**
 * Fetch normalized Passport data for a CV.
 * Returns null on 404 (no CV text yet); throws on other errors.
 */
export async function getCvPassport(apiCall: ApiCall, cvId: string): Promise<CvPassport | null> {
  const res = await apiCall(`${apiBase()}/api/v1/cv/${cvId}/passport`);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`Failed to load passport (${res.status})`);
  }
  return (await res.json()) as CvPassport;
}

export interface CvPublicProfile {
  cv_id: string;
  real_talk_sentence: string | null;
  real_talk_tags: string[] | null;
  is_contactable: boolean;
}

/**
 * Fetch the anonymized public profile bits for a CV (Real Talk + contactability).
 * Returns null on 404; throws on other errors.
 */
export async function getCvPublicProfile(apiCall: ApiCall, cvId: string): Promise<CvPublicProfile | null> {
  const res = await apiCall(`${apiBase()}/api/v1/profiles/by-cv-id/${cvId}/public`);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`Failed to load public profile (${res.status})`);
  }
  const data = await res.json();
  return {
    cv_id: cvId,
    real_talk_sentence: data.real_talk_sentence ?? null,
    real_talk_tags: data.real_talk_tags ?? null,
    is_contactable: !!data.is_contactable,
  };
}
